/* =============================================================================
 * OPTIS (BSS Product Options) 在庫連動スクリプト
 * =============================================================================
 * BSS Product Options の選択肢について、親SKU在庫が不足している場合に
 * 選択不可（disabled + グレーアウト）にする。
 *
 * 設置: テーマの assets/optis_stock_control.js に置き、theme.liquid の <body> 直後で読み込む。
 *   <script src="{{ 'optis_stock_control.js' | asset_url }}"
 *     data-config-endpoint="https://shopify-skusync.stdu.pw/public_config.php" defer></script>
 *   data-config-endpoint の代わりに window.__OPTIS_STOCK_CONFIG_ENDPOINT を先に定義してもよい。
 *
 * 正規化ルール（PHP normalize_optis_name() と一致）:
 *   【】除去 / （）除去 / 空白除去 / 小文字化
 * ============================================================================= */

(function () {
  "use strict";

  // ---- 設定エンドポイント ----
  const currentScript = document.currentScript || document.querySelector("script[data-config-endpoint]");
  const CONFIG_ENDPOINT = (currentScript && currentScript.getAttribute("data-config-endpoint")) || window.__OPTIS_STOCK_CONFIG_ENDPOINT || null;

  if (!CONFIG_ENDPOINT) {
    console.warn("[optis_stock_control] config endpoint not set");
    return;
  }

  const SOLDOUT_LABEL = "売り切れ";
  // config.php の sku_pattern と一致させること
  const SKU_PATTERN = /^([A-Z0-9\-]+?)(\d+)$/;
  const SWATCH_WRAPPER = ".po-option--swatch-wrapper";
  const SWATCH_INPUT = '.po-option--swatch-wrapper input[type="checkbox"], .po-option--swatch-wrapper input[type="radio"]';

  let state = {
    optionMap: null,
    originalStock: null, // public_config.php から取得したままの在庫値（不変）
    stock: null, // 商品ページ用: カート需要を差し引いた値
    bundleConfigs: null,
    readyMarked: false,
  };

  // ===========================================================================
  // ユーティリティ
  // ===========================================================================

  function normalizeOptisName(s) {
    if (!s) return "";
    return String(s)
      .replace(/【[^】]*】/g, "")
      .replace(/[（(][^）)]*[）)]/g, "")
      .replace(/[\s\u3000]+/g, "")
      .toLowerCase()
      .trim();
  }

  // "2 x コリアンダーブラック, 6 x コリアンダーホワイト（季節限定） (+ ¥55)" → [{qty, name}]
  function parseSelectionText(text) {
    if (!text) return [];
    const items = [];
    for (const part of String(text).split(/,\s*/)) {
      const m = part.match(/^(\d+)\s*x\s*(.+)/i);
      if (m) {
        // 価格サフィックス "(+ ¥55)" を除去してから正規化
        items.push({ qty: parseInt(m[1], 10), name: m[2].replace(/\s*\(\+[^)]*\)\s*$/, "").trim() });
      }
    }
    return items;
  }

  // /cart.js から SKU別需要を集計
  // Shopify の在庫判定はバリアント単位なので、同じ親の3本入りと6本入りを同時に入れると
  // それぞれ単独では通ってしまう。親SKU単位で合算して判定する。
  function computeCartDemand(cartItems) {
    if (!state.optionMap) return {};
    const demand = {};
    const add = (parentSku, qty, optionName, title) => {
      if (!demand[parentSku]) demand[parentSku] = { needed: 0, items: [] };
      demand[parentSku].needed += qty;
      demand[parentSku].items.push({ optionName, qty, title });
    };
    for (const item of cartItems) {
      const orderQty = item.quantity || 1;
      const pack = SKU_PATTERN.exec(item.sku || "");
      if (pack && state.originalStock[pack[1]] !== undefined) {
        add(pack[1], Number(pack[2]) * orderQty, item.title, item.title);
      }

      const props = item.properties || {};
      let selectionText = null;
      for (const [key, val] of Object.entries(props)) {
        if (key.startsWith("_")) continue;
        if (/ビール選択|お好きなビール/.test(key)) {
          selectionText = val;
          break;
        }
      }
      if (!selectionText) continue;
      const selections = parseSelectionText(selectionText);
      for (const sel of selections) {
        const nk = normalizeOptisName(sel.name);
        const parentSku = state.optionMap[nk];
        if (!parentSku) continue;
        add(parentSku, sel.qty * orderQty, sel.name, item.title);
      }
    }
    return demand;
  }

  async function findStockWarnings() {
    if (!state.originalStock) return [];
    const cart = await fetch("/cart.js").then((r) => r.json()).catch(() => null);
    if (!cart) return [];
    const warnings = [];
    for (const [sku, info] of Object.entries(computeCartDemand(cart.items || []))) {
      // originalStock を使う（商品ページでの差引の影響を受けない）
      const available = Number(state.originalStock[sku] || 0);
      if (available <= 0 || info.needed > available) {
        warnings.push({ sku, needed: info.needed, available: Math.max(0, available), items: info.items });
      }
    }
    return warnings;
  }

  function renderWarningItems(warnings) {
    return warnings
      .map((w) => `<li style="margin:2px 0;"><strong>${w.items.map((i) => i.optionName).join("、")}</strong>（ご注文 ${w.needed}本 / 在庫 ${w.available}本）</li>`)
      .join("");
  }

  // ===========================================================================
  // 設定取得
  // ===========================================================================

  async function loadConfig() {
    try {
      const res = await fetch(CONFIG_ENDPOINT, { credentials: "omit" });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const json = await res.json();
      state.optionMap = json.optionMap || {};
      state.originalStock = json.stock || {};
      state.stock = { ...state.originalStock }; // コピー（商品ページで書き換え可）
      state.bundleConfigs = json.bundleConfigs || {};
      return true;
    } catch (e) {
      console.warn("[optis_stock_control] failed to load config", e);
      return false;
    }
  }

  // ===========================================================================
  // 商品ページ: ブロッキングUI
  // ===========================================================================

  function injectBlockingStyle() {
    if (document.getElementById("optis-stock-block-style")) return;
    const style = document.createElement("style");
    style.id = "optis-stock-block-style";
    style.textContent = `
      @keyframes optis-stock-spin { to { transform: rotate(360deg); } }
      @keyframes optis-stock-pulse { 0%,100% { opacity:.55 } 50% { opacity:.85 } }
      body:not(.optis-stock-ready) .bss-po-option-set {
        position: relative; min-height: 80px;
      }
      body:not(.optis-stock-ready) .bss-po-option-set::after {
        content: ""; position: absolute; inset: 0;
        background: rgba(255,255,255,0.6); backdrop-filter: blur(1px);
        z-index: 9998; pointer-events: all; cursor: wait;
      }
      body:not(.optis-stock-ready) .bss-po-option-set::before {
        content: "在庫情報を確認しています…";
        position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
        z-index: 9999; display: flex; align-items: center; gap: 10px;
        padding: 10px 18px; background: #fff; border: 1px solid #ddd;
        border-radius: 999px; font-size: 13px; font-weight: 600; color: #333;
        box-shadow: 0 4px 16px rgba(0,0,0,0.12); white-space: nowrap;
      }
      body:not(.optis-stock-ready) .po-option--swatch-wrapper,
      body:not(.optis-stock-ready) .po-option--button-wrapper {
        pointer-events: none !important; animation: optis-stock-pulse 1.2s ease-in-out infinite;
      }
      body:not(.optis-stock-ready) .optis-stock-spinner {
        position: absolute; top: 50%; left: 50%;
        transform: translate(calc(-50% - 92px),-50%);
        z-index: 10000; width: 14px; height: 14px;
        border: 2px solid #ddd; border-top-color: #c0392b;
        border-radius: 50%; animation: optis-stock-spin .7s linear infinite;
        pointer-events: none;
      }
      .po-option--soldout { position: relative; }
    `;
    (document.head || document.documentElement).appendChild(style);

    const addSpinner = () => {
      document.querySelectorAll(".bss-po-option-set").forEach((el) => {
        if (el.querySelector(":scope > .optis-stock-spinner")) return;
        const sp = document.createElement("div");
        sp.className = "optis-stock-spinner";
        el.appendChild(sp);
      });
    };
    addSpinner();
    const spObs = new MutationObserver(addSpinner);
    spObs.observe(document.body, { childList: true, subtree: true });
    const tid = setInterval(() => {
      if (document.body.classList.contains("optis-stock-ready")) {
        document.querySelectorAll(".optis-stock-spinner").forEach((n) => n.remove());
        spObs.disconnect();
        clearInterval(tid);
      }
    }, 150);
  }

  function markReady() {
    if (state.readyMarked) return;
    state.readyMarked = true;
    document.body.classList.add("optis-stock-ready");
  }

  // ===========================================================================
  // 商品ページ: オプション制御
  // ===========================================================================

  function applyToInput(input) {
    if (!state.optionMap || !state.stock) return;
    if (input.dataset.stockChecked === "1") return;

    const key = normalizeOptisName(input.value || "");
    if (!key) return;
    const parentSku = state.optionMap[key];
    if (!parentSku) return;

    const available = Number(state.stock[parentSku] || 0);
    input.dataset.stockChecked = "1";
    if (available > 0) return;

    input.disabled = true;
    input.checked = false;
    const wrapper = input.closest(SWATCH_WRAPPER);
    if (wrapper) {
      wrapper.style.opacity = "0.4";
      wrapper.style.pointerEvents = "none";
      wrapper.classList.add("po-option--soldout");
      const desc = wrapper.querySelector(".po_description");
      if (desc && !desc.querySelector(".po-soldout-label")) {
        const p = document.createElement("p");
        p.className = "po-soldout-label";
        p.textContent = SOLDOUT_LABEL;
        p.style.cssText = "color:#c0392b;font-size:11px;font-weight:bold;margin-top:4px;text-align:center;";
        desc.appendChild(p);
      }
    }
  }

  function enforceBundleMultiplierAll() {
    if (!state.bundleConfigs || !state.stock) return;
    document.querySelectorAll('.po-option--button-wrapper input[type="radio"]').forEach((input) => {
      if (input.dataset.bundleChecked === "1") return;
      const val = (input.value || "").trim();
      for (const [, cfg] of Object.entries(state.bundleConfigs)) {
        const multiplier = cfg.multipliers[val];
        if (multiplier === undefined) continue;
        input.dataset.bundleChecked = "1";

        let insufficient = false;
        for (const [parentSku, baseQty] of Object.entries(cfg.baseItems)) {
          if (Number(state.stock[parentSku] || 0) < Number(baseQty) * Number(multiplier)) {
            insufficient = true;
            break;
          }
        }
        if (insufficient) {
          input.disabled = true;
          const wrapper = input.closest(".po-option--button-wrapper");
          if (wrapper) {
            wrapper.style.opacity = "0.4";
            wrapper.style.pointerEvents = "none";
            wrapper.classList.add("po-option--soldout");
            const media = wrapper.querySelector(".po-option__button-media");
            if (media && !media.querySelector(".po-soldout-label")) {
              const p = document.createElement("div");
              p.className = "po-soldout-label";
              p.textContent = "入荷待ち";
              p.style.cssText = "color:#c0392b;font-size:10px;font-weight:bold;";
              media.appendChild(p);
            }
          }
          if (input.checked) {
            input.checked = false;
            const fb = document.querySelector(`input[name="${input.name}"]:not(:disabled)`);
            if (fb) { fb.checked = true; fb.dispatchEvent(new Event("change", { bubbles: true })); }
          }
        }
        break;
      }
    });
  }

  function enforceQuantityMaxAll() {
    if (!state.optionMap || !state.stock) return;
    document.querySelectorAll(SWATCH_WRAPPER).forEach((wrapper) => {
      const qtyInput = wrapper.querySelector(".quantity-control .quantity-input");
      if (!qtyInput || qtyInput.dataset.stockMaxSet === "1") return;

      const cb = wrapper.querySelector('input[type="checkbox"], input[type="radio"]');
      if (!cb) return;
      const parentSku = state.optionMap[normalizeOptisName(cb.value || "")];
      if (!parentSku) return;

      const available = Math.max(0, Number(state.stock[parentSku] || 0));
      if (available <= 0) return;

      qtyInput.max = String(available);
      qtyInput.dataset.stockMaxSet = "1";

      const showMsg = () => {
        let msg = wrapper.querySelector(".po-stock-max-msg");
        if (!msg) {
          msg = document.createElement("p");
          msg.className = "po-stock-max-msg";
          msg.style.cssText = "color:#c0392b;font-size:11px;font-weight:bold;margin-top:4px;text-align:center;";
          (wrapper.querySelector(".po_description") || wrapper).appendChild(msg);
        }
        msg.textContent = `在庫上限: ${available}本`;
      };

      const clamp = () => {
        if ((parseInt(qtyInput.value, 10) || 0) > available) {
          qtyInput.value = String(available);
          showMsg();
          qtyInput.dispatchEvent(new Event("input", { bubbles: true }));
          qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      };

      qtyInput.addEventListener("input", clamp);
      qtyInput.addEventListener("change", clamp);

      const plusBtn = wrapper.querySelector(".quantity-control .quantity-plus");
      if (plusBtn) {
        plusBtn.addEventListener("click", (e) => {
          if ((parseInt(qtyInput.value, 10) || 0) >= available) {
            e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
            showMsg();
          } else {
            setTimeout(clamp, 0);
          }
        }, true);
      }
      clamp();
    });
  }

  // バンドル商品で全本数ボタンが無効 or 全オプションが在庫切れなら、カートに入れるボタンも無効化
  function disableAddToCartIfNeeded() {
    // 本数ボタンが1つでも存在する場合、全部disabledならカート追加不可
    const bundleBtns = document.querySelectorAll('.po-option--button-wrapper input[type="radio"]');
    if (bundleBtns.length > 0) {
      const allDisabled = [...bundleBtns].every((b) => b.disabled);
      if (allDisabled) {
        disableProductAddToCart("選択可能な本数がありません");
        return;
      }
    }

    // チェックボックス型（アソートBOX）: 選択可能なオプションが0個なら無効化
    const checkboxes = document.querySelectorAll(SWATCH_INPUT);
    if (checkboxes.length > 0) {
      const allSoldOut = [...checkboxes].every((cb) => cb.disabled);
      if (allSoldOut) {
        disableProductAddToCart("選択可能な商品がありません");
        return;
      }
    }
  }

  function disableProductAddToCart(reason) {
    // BSS_PO の po-atc-btn + Shopify標準の product-form__submit + name="add" を全て対象
    const btns = document.querySelectorAll(
      '.po-atc-btn, .product-form__submit, [name="add"], button[type="submit"][po-btn-atc]'
    );
    btns.forEach((btn) => {
      btn.disabled = true;
      btn.style.opacity = "0.4";
      btn.style.pointerEvents = "none";
    });
    // 理由表示（ボタンの近くに1回だけ）
    const form = document.querySelector('form[action="/cart/add"], .shopify-product-form, .main-product-form');
    if (form && !form.querySelector(".optis-add-disabled-msg")) {
      const msg = document.createElement("p");
      msg.className = "optis-add-disabled-msg";
      msg.style.cssText = "color:#c0392b;font-size:12px;font-weight:bold;margin-top:8px;text-align:center;";
      msg.textContent = reason;
      form.appendChild(msg);
    }
  }

  function scanAll() {
    if (!state.optionMap) return;
    document.querySelectorAll(SWATCH_INPUT).forEach(applyToInput);
    enforceQuantityMaxAll();
    enforceBundleMultiplierAll();
    disableAddToCartIfNeeded();
  }

  // ===========================================================================
  // 商品ページ: 初期化
  // ===========================================================================

  function waitForOptisOptions(timeout) {
    return new Promise((resolve) => {
      const sel = ".po-option--swatch-wrapper, .po-option--button-wrapper";
      if (document.querySelector(sel)) return resolve(true);
      let done = false;
      const obs = new MutationObserver(() => {
        if (document.querySelector(sel)) { done = true; obs.disconnect(); resolve(true); }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => { if (!done) { obs.disconnect(); resolve(false); } }, timeout);
    });
  }

  async function init() {
    const configPromise = loadConfig();

    const hasOptions = await waitForOptisOptions(3000);
    if (!hasOptions) return;

    injectBlockingStyle();
    const failsafe = setTimeout(() => { markReady(); }, 5000);

    const ok = await configPromise;
    if (!ok) { clearTimeout(failsafe); markReady(); return; }

    // カート内の既存需要を在庫から差し引く（state.stock のみ。originalStock は不変）
    try {
      const cart = await fetch("/cart.js").then((r) => r.json()).catch(() => null);
      if (cart && cart.items) {
        const demand = computeCartDemand(cart.items);
        for (const [sku, info] of Object.entries(demand)) {
          if (state.stock[sku] !== undefined) {
            state.stock[sku] = Number(state.stock[sku]) - info.needed;
          }
        }
      }
    } catch (_) { /* カート取得失敗は無視 */ }

    scanAll();

    // OPTIS 遅延描画 / 本数切替時の再描画を監視
    const watchSel = ".po-option--swatch-wrapper, .po-option--button-wrapper, .quantity-control";
    const obs = new MutationObserver((muts) => {
      let hit = false;
      for (const m of muts) {
        for (const n of m.addedNodes || []) {
          if (n.nodeType === 1 && (n.matches?.(watchSel) || n.querySelector?.(watchSel))) { hit = true; break; }
        }
        if (hit) break;
      }
      if (hit) {
        scanAll();
        if (document.querySelector(".po-option--swatch-wrapper")) { clearTimeout(failsafe); markReady(); }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    if (document.querySelector(".po-option--swatch-wrapper")) { clearTimeout(failsafe); markReady(); }

    // 本数切替時の再描画対策（ready後は停止）
    let retries = 0;
    const retryTid = setInterval(() => {
      scanAll();
      if (++retries >= 5 || state.readyMarked) clearInterval(retryTid);
    }, 800);
  }

  // ===========================================================================
  // カートページ: バリデーション + オプション編集監視
  // ===========================================================================

  async function cartValidation() {
    if (!state.optionMap && !(await loadConfig())) return;

    watchCartEditOverlay();

    const warnings = await findStockWarnings();
    if (warnings.length > 0) showCartWarning(warnings);
  }

  function showCartWarning(warnings) {
    const old = document.getElementById("optis-cart-stock-warning");
    if (old) old.remove();

    const banner = document.createElement("div");
    banner.id = "optis-cart-stock-warning";
    banner.style.cssText = "background:#fff;border:1px solid #c0392b;border-radius:12px;padding:16px 20px;margin:16px 0;font-size:14px;line-height:1.6;";

    banner.innerHTML =
      '<p style="margin:0 0 8px;font-weight:bold;color:#c0392b;font-size:15px;">在庫が不足している商品があります</p>' +
      `<ul style="margin:0;padding-left:20px;">${renderWarningItems(warnings)}</ul>` +
      '<p style="margin:8px 0 0;font-size:12px;color:#666;">数量を減らすか、「オプションを編集する」から選択内容を変更してください。</p>';

    const form = document.querySelector(".cart__form, #cart, form[action='/cart']");
    if (form) form.parentNode.insertBefore(banner, form);
    else document.querySelector(".cart, #MainContent")?.prepend(banner);

    // チェックアウト系ボタンを無効化
    document.querySelectorAll('button[name="checkout"], input[name="checkout"], .cart__checkout-button, a[href="/checkout"]').forEach((btn) => {
      btn.style.opacity = "0.4"; btn.style.pointerEvents = "none";
      if (btn.tagName === "BUTTON" || btn.tagName === "INPUT") btn.disabled = true;
    });
    document.querySelectorAll(".additional-checkout-buttons, .dynamic-checkout__content, shopify-accelerated-checkout-cart").forEach((el) => {
      el.style.opacity = "0.4"; el.style.pointerEvents = "none"; el.style.filter = "grayscale(1)";
    });
  }

  // ===========================================================================
  // カート内「オプションを編集する」オーバーレイ監視
  // ===========================================================================

  function watchCartEditOverlay() {
    const SEL = "#po-edit-option";
    let lastTime = 0;
    const obs = new MutationObserver(() => {
      const c = document.querySelector(SEL);
      if (!c || !c.querySelector(".po-option--swatch-wrapper, .po-option--button-wrapper")) return;
      const now = Date.now();
      if (now - lastTime < 500) return;
      lastTime = now;
      setTimeout(() => {
        c.querySelectorAll("[data-stock-checked],[data-stock-max-set],[data-bundle-checked]").forEach((el) => {
          el.removeAttribute("data-stock-checked"); el.removeAttribute("data-stock-max-set"); el.removeAttribute("data-bundle-checked");
        });
        c.querySelectorAll(".po-soldout-label, .po-stock-max-msg").forEach((el) => el.remove());
        c.querySelectorAll(".po-option--soldout").forEach((el) => {
          el.classList.remove("po-option--soldout"); el.style.opacity = ""; el.style.pointerEvents = "";
        });
        scanAll();
      }, 300);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ===========================================================================
  // カートドロワー: 在庫チェック（全ページ共通）
  // ===========================================================================

  const DRAWER_ID = "CartDrawer";

  function showDrawerWarning(drawer, warnings) {
    const old = drawer.querySelector("#optis-drawer-stock-warning");
    if (old) old.remove();

    const banner = document.createElement("div");
    banner.id = "optis-drawer-stock-warning";
    banner.style.cssText = "background:#fff;border:1px solid #c0392b;border-radius:12px;padding:12px 16px;margin:8px 0;font-size:13px;line-height:1.5;";
    banner.innerHTML =
      '<p style="margin:0 0 6px;font-weight:bold;color:#c0392b;font-size:12px;">在庫が不足している商品があります</p>' +
      `<ul style="margin:0;padding-left:16px;">${renderWarningItems(warnings)}</ul>` +
      '<p style="margin:6px 0 0;font-size:10px;">お手数ですが、「かごの中身を見る」から、数量またはオプションの選択内容を変更してください</p>';

    // hidden でない drawer__body を選ぶ
    const bodies = drawer.querySelectorAll(".cart-drawer__body, .drawer__body");
    let target = null;
    for (const db of bodies) { if (!db.classList.contains("hidden")) { target = db; break; } }
    if (!target && bodies.length) target = bodies[bodies.length - 1];
    if (target) target.prepend(banner);

    drawer.querySelectorAll('button[name="checkout"], a[href="/checkout"]').forEach((btn) => {
      btn.style.opacity = "0.4"; btn.style.pointerEvents = "none";
      if (btn.disabled !== undefined) btn.disabled = true;
    });
  }

  function watchCartDrawer() {
    // ドロワーが存在しなければ監視しない
    if (!document.getElementById(DRAWER_ID)) return;

    const check = async () => {
      const drawer = document.getElementById(DRAWER_ID);
      if (!drawer || !drawer.querySelector(".cart-item")) return;

      if (!state.optionMap) await loadConfig();
      const warnings = await findStockWarnings();
      if (warnings.length > 0) showDrawerWarning(drawer, warnings);
    };

    // 2秒ポーリングでドロワーの開閉を検知
    let wasVisible = false;
    setInterval(() => {
      const drawer = document.getElementById(DRAWER_ID);
      if (!drawer) return;
      const rect = drawer.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0 && !drawer.hasAttribute("hidden");
      if (isVisible && !wasVisible) setTimeout(check, 300);
      if (!isVisible && wasVisible) {
        const w = drawer.querySelector("#optis-drawer-stock-warning");
        if (w) w.remove();
      }
      wasVisible = isVisible;
    }, 2000);
  }

  // ===========================================================================
  // チェックアウト送信ゲート
  // ===========================================================================

  // カート画面で数量を変えた後は再チェックが走らないため、送信直前にも判定する
  function guardCheckoutSubmit() {
    document.addEventListener("submit", async (e) => {
      const btn = e.submitter;
      if (!btn || btn.name !== "checkout" || btn.dataset.stockVerified === "1") return;
      e.preventDefault();
      // 確認に 1〜2 秒かかるので、押したことが分かるようにし、二度押しで確認を重ねない
      if (btn.getAttribute("aria-busy") === "true") return;
      btn.setAttribute("aria-busy", "true");
      let leaving = false;
      try {

      // 設定取得に失敗したときは止めない（在庫APIの障害で購入全体を止めないため）
      const warnings = (await loadConfig()) ? await findStockWarnings() : [];
      if (warnings.length === 0) {
        // カートで指定した配達希望日が、日をまたいで過去になったまま購入されるのを防ぐ
        const delivery = await window.ShipAndCoDeliveryGuard?.run().catch((err) => {
          console.warn("[optis_stock_control] delivery guard failed", err);
          return null;
        });
        if (delivery?.status === "reset") {
          alert("配達希望日が過去の日付になっていたため、指定を解除しました。カート画面で配達希望日・時間帯を指定し直してください。");
          leaving = true;
          location.assign(`${window.Shopify?.routes?.root || "/"}cart`);
          return;
        }

        btn.dataset.stockVerified = "1";
        leaving = true;
        btn.click();
        delete btn.dataset.stockVerified;
        return;
      }
      const drawer = btn.closest("#" + DRAWER_ID);
      if (drawer) showDrawerWarning(drawer, warnings);
      else showCartWarning(warnings);
      } finally {
        if (!leaving) btn.removeAttribute("aria-busy");
      }
    }, true);

    const style = document.createElement("style");
    style.textContent = 'button[name="checkout"][aria-busy="true"]{opacity:.6;cursor:progress}';
    document.head.appendChild(style);
    // 「戻る」でページが復元されたとき、押した状態のまま残さない
    window.addEventListener("pageshow", (e) => {
      if (!e.persisted) return;
      document.querySelectorAll('button[name="checkout"][aria-busy]').forEach((b) => b.removeAttribute("aria-busy"));
    });
  }

  // ===========================================================================
  // エントリポイント
  // ===========================================================================

  function start() {
    guardCheckoutSubmit();
    watchCartDrawer();

    if (document.body.classList.contains("cart-template") || /^\/cart\/?$/.test(location.pathname)) {
      cartValidation();
    } else {
      init();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
